import { AxiosResponse } from 'axios';
import { LdJsonBreadCrumbsResort } from 'components/LdJson';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React from 'react';
import { Routes } from 'routes';

import { getResortSlug } from 'util/memcache';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortRelated,
  fetchResortReports,
  fetchResortShortInfo, fetchResortShortWeatherReport,
} from 'api/resort/static';
import {
  IResortReports, IResortPathInfo, IResortPageInfo,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { BlockContent } from 'components/blocks/BlockContent';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { ReportsList } from 'components/resort/ReportsList';
import { Hero400Error } from 'components/400';
import { fetchResortMeta } from 'api/region/static';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { Side } from 'components/resort/Layout/Side';
import { SidebarAd } from 'googlead/components/SidebarAd';

export type ResortFHPageProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  resortReports: IResortReports | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-firsthand-page';
export const TEMPLATE_ID = '2.2.4';
export const TEMPLATE_NAME = 'Resort Ski Report';

export default function ResortFHPage({
  resort, pathInfo, resortReports,
}: ResortFHPageProps) {
  if (!resort) {
    return <Hero400Error light />;
  }

  const { t } = useOTSTranslation('common');

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.fhr.seoTitle', { resort: resort.title })} />
          <BlockContent>
            {t('resort.fhr.seoText', { resort: resort.title })}
          </BlockContent>
        </BoxWithShadow>
        {resortReports && <ReportsList resortList={resortReports} uuid={pathInfo.uuid} />}
        <LdJsonBreadCrumbsResort
          resort={pathInfo}
          region={resort.region}
          fn={Routes.resortFHReports}
        />
      </Main>
      <Side>
        <SidebarAd />
      </Side>
    </>
  );
}

export const fetchProps = async ({ resort }:{ region: string, resort: string }): Promise<ResortFHPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));

  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const resortReports = await fetchResortReports(uuid, 0);

  return {
    type: 'resort',
    route: 'firsthand',
    resort: res,
    pathInfo: cache.data,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    resortReports,

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.FHR,
      tab: CurrentTab.SNOW,
      head: HeaderNavCurLink.SNOW_REPORTS,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort ski_report_reviews',
    hero: {
      title: 'resort.fhr.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.ski_report_reviews.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.ski_report_reviews.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
  };
};

// This function gets called at build time
export const getResortFHPageStaticProps = wrapStaticProps(fetchProps);

export const getResortFHPageServerSideProps = wrapServerSideProps(fetchProps);
